
import { mobileSupabase as supabase } from '@/integrations/supabase/mobile-client';
import { getUserFamilyId } from './helpers';
import { logger } from '@/utils/logger';

// Child type
export interface Child {
  id: string;
  name: string;
  birthdate: string;
  photoUrl?: string;
}

// Upload photo to Supabase Storage
const uploadChildPhoto = async (file: File, childId: string): Promise<string | null> => {
  try {
    const { data: userData } = await supabase.auth.getUser();
    if (!userData?.user) {
      logger.error('No authenticated user for photo upload');
      return null;
    }

    const fileExt = file.name.split('.').pop();
    const fileName = `${childId}-${Date.now()}.${fileExt}`;
    const filePath = `${userData.user.id}/children/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('child-photos')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: true
      });

    if (uploadError) {
      logger.error('Error uploading photo:', uploadError);
      return null;
    }

    const { data: urlData } = supabase.storage
      .from('child-photos')
      .getPublicUrl(filePath);

    return urlData.publicUrl;
  } catch (error) {
    logger.error('Error in uploadChildPhoto:', error);
    return null;
  }
};

// Children storage functions
export const getStoredChildren = async (): Promise<Child[]> => {
  try {
    const { data: user } = await supabase.auth.getUser();
    if (!user?.user) {
      return [];
    }
    
    const familyId = await getUserFamilyId();
    if (!familyId) return [];
    
    const { data, error } = await supabase
      .from('children')
      .select('*')
      .eq('family_id', familyId)
      .order('name');

    if (error) {
      logger.error('Error fetching children:', error);
      return [];
    }

    if (!data) return [];

    return data.map(child => ({
      id: child.id,
      name: child.name,
      birthdate: child.birthdate,
      photoUrl: child.photo_url
    }));
  } catch (error) {
    logger.error('Error in getStoredChildren:', error);
    return [];
  }
};

export const storeChildren = async (
  child: Omit<Child, 'id'>,
  photo?: File
): Promise<Child | null> => {
  try {
    const { data: userData } = await supabase.auth.getUser();
    if (!userData?.user) {
      logger.error('No authenticated user found');
      return null;
    }

    const familyId = await getUserFamilyId();
    if (!familyId) {
      logger.error('User has no family');
      return null;
    }

    // First create the child record
    const { data, error } = await supabase
      .from('children')
      .insert({
        name: child.name,
        birthdate: child.birthdate,
        photo_url: child.photoUrl,
        user_id: userData.user.id,
        family_id: familyId
      })
      .select()
      .single();

    if (error) {
      logger.error('Error storing child:', error);
      return null;
    }

    if (!data) return null;

    // If a photo was provided, upload it and update the record
    let photoUrl = data.photo_url;
    if (photo) {
      const uploadedUrl = await uploadChildPhoto(photo, data.id);
      if (uploadedUrl) {
        const { error: updateError } = await supabase
          .from('children')
          .update({ photo_url: uploadedUrl })
          .eq('id', data.id);

        if (updateError) {
          logger.error('Error updating photo URL:', updateError);
        } else {
          photoUrl = uploadedUrl;
        }
      }
    }

    return {
      id: data.id,
      name: data.name,
      birthdate: data.birthdate,
      photoUrl: photoUrl
    };
  } catch (error) {
    logger.error('Error in storeChildren:', error);
    return null;
  }
};
